// FAQ and Glossary data types and constants

export type UserLevel = 'all' | 'beginner' | 'intermediate' | 'advanced';
export type Relevance = 'high' | 'medium' | 'low';

export interface FAQItem {
  type: 'faq';
  question: string;
  answer: string;
  relevance: Relevance;
  userLevel: UserLevel;
}

export interface GlossaryItem {
  type: 'glossary';
  term: string;
  definition: string;
  relevance: Relevance;
  userLevel: UserLevel;
}

export type FAQOrGlossaryItem = FAQItem | GlossaryItem;

const faqAndGlossaryData = [
  {
    "type": "faq",
    "question": "What is SMA?",
    "answer": "Seattle Market Analytics (SMA) is an investment-intelligence app that ingests fundamentals, technicals, machine-learning forecasts, and institutional-holding data for roughly 2,600 U.S. stocks after every trading session. Overnight it produces: (1) stock-level ratings, rankings, price-targets, and risk metrics; (2) Market Reports that roll those numbers up for the whole market, every sector, and each market-cap bucket; (3) Factor Reports that grade Money-Flow, Performance, and Risk; and (4) interactive heat-maps. All of this is automatically tailored to five built-in investment profiles so that the guidance fits your style.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "faq",
    "question": "How does SMA decide whether a stock is a BUY, HOLD, or SELL?",
    "answer": "For each horizon—Short-Term (≈1 mo), Mid-Term (≈4.5 mo), and Long-Term (≈1 yr)—SMA blends valuation, momentum, mean-reversion signals, ML-projected returns, and relative-strength versus sector & SPY. A filter layer blocks stocks that fail basic liquidity or extreme-volatility checks, so one great factor can't hide serious red flags. The weighted composite becomes the Rating (Strong Buy ↔ Strong Sell) and the cross-ticker Ranking.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "faq",
    "question": "How do I get started with SMA?",
    "answer": "To get started with SMA: 1) Create an account through the sign-up process, 2) Choose your subscription tier based on your needs (Free, Base, Premium, or Premium Plus), 3) Set up your investment profile that matches your investment strategy and risk appetite, 4) Create watchlists and screeners to track stocks of interest, and 5) Begin exploring market reports and stock rankings to inform your investment decisions.",
    "relevance": "high",
    "userLevel": "beginner"
  },
  {
    "type": "faq",
    "question": "What subscription tiers are available?",
    "answer": "SMA offers several subscription tiers: FREE (basic access at no cost), BASE ($10/month for essential features), PREMIUM ($20/month for advanced features including investment profiles and watchlists), and PREMIUM_PLUS ($50/month for full platform access including advanced screening and heatmap features). Each tier provides increasing levels of functionality and data access.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "faq",
    "question": "What is an Investment Profile?",
    "answer": "An Investment Profile represents your investment approach and preferences. It includes parameters like time horizon (short-term, mid-term, long-term), risk appetite (conservative, moderate, aggressive), and strategy (value, growth, income). SMA uses your profile to customize recommendations, watchlists, and screeners to align with your investment goals. Premium users can create multiple profiles for different investment objectives.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "faq",
    "question": "What's the difference between watchlists and screeners?",
    "answer": "Watchlists are manually curated lists of stocks you want to track. You select specific tickers to add to your watchlist and can organize them by investment profile. Screeners are automated tools that filter stocks based on specific criteria or rules. SMA offers two types of screeners: Pandas-based (using quantitative rules) and AI-based (using natural language to describe desired stock characteristics). Watchlists are for tracking known stocks of interest, while screeners help you discover new investment opportunities.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "faq",
    "question": "How often is market data updated?",
    "answer": "All data is re-ingested after each market close and the entire analytics pipeline reruns overnight. Fresh reports and heat-maps are ready in the app by 6 a.m. ET the next trading day.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "faq",
    "question": "What is a Market Report?",
    "answer": "A Market Report is a comprehensive analysis of current market conditions, trends, and projections across different sectors and market capitalizations. It includes ratings (Bullish, Moderately Bullish, Neutral, Moderately Bearish, Bearish), trend analysis, and specific investment recommendations tailored to your investment profile. The report analyzes money flow, performance, and risk factors to provide a holistic view of market conditions and help guide your allocation decisions.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "faq",
    "question": "What time horizons does SMA analyze?",
    "answer": "SMA analyzes three primary time horizons: Short-Term (approximately 1 month), Mid-Term (approximately 4.5 months), and Long-Term (approximately 1 year). Each horizon has its own set of predictions, ratings, and analysis to help users make decisions aligned with their investment timeframe.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "faq",
    "question": "How do I interpret the investment ratings?",
    "answer": "Investment ratings in SMA appear in various forms depending on the context. For overall market or sector outlook: BULLISH, MODERATELY BULLISH, NEUTRAL, MODERATELY BEARISH, or BEARISH. For risk assessment: LOW, MEDIUM-LOW, MEDIUM-HIGH, or HIGH. For money flow: STRONG INFLOW, INFLOW, FLAT, OUTFLOW, or STRONG OUTFLOW. For performance: STRONG GAIN, GAIN, FLAT, LOSS, or STRONG LOSS. These ratings help you understand conditions and align your investment decisions with current market dynamics.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "faq",
    "question": "Which investment profiles can I choose and why do they matter?",
    "answer": "SMA offers several investment profile templates to match your investment style: Ultra-Aggressive Active Trader (daily rebalance), Aggressive Growth Investor (bi-weekly to monthly), Balanced Growth Investor (quarterly), Quality Income Investor (quarterly), and Value Investor (quarterly). Each profile carries its own risk budget, rebalance cadence, and default screeners, so every Market or Factor Report you open is automatically phrased for that philosophy. These templates are designed for different risk tolerances and investment goals.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "faq",
    "question": "What are Market Reports, Factor Reports, and Heat-maps?",
    "answer": "• Market Reports bundle the daily stock analytics into an all-up outlook (rating, regime, trend and suggested equity vs cash range) plus break-outs by sector and market-cap.\n• Factor Reports zoom in on Money-Flow, Performance, and Risk, again sliced market-wide, by sector, and by cap.\n• Heat-maps colour-code historical and projected returns so you can spot momentum or value pockets at a glance.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "faq",
    "question": "How do watchlists and screeners work inside the app?",
    "answer": "Every watchlist or screener is tied to a chosen investment profile (and optionally a brokerage account). Screeners use drag-and-drop filters or preset AI templates—no coding required—to run logical rules such as \"Strong Buy & outperforming sector for one month.\" Your default screener always mirrors the active profile's horizon, risk, and strategy.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "faq",
    "question": "How do I create a custom screener?",
    "answer": "Choose \"New Screener,\" pick a preset template (e.g., \"Top 10 Gainers\") or build your own by stacking visual filters: rating, relative strength, volatility band, dividend yield, etc. Save it to run daily or on-demand. The query rule behind the scenes is handled for you.",
    "relevance": "medium",
    "userLevel": "intermediate"
  },
  {
    "type": "faq",
    "question": "What is a Market Regime?",
    "answer": "A Market Regime is a classification of current market conditions into distinct phases of market cycles. SMA identifies regimes such as 'Bull Market (Early/Strong Phase)', 'Bull Market (Mature/Late Phase)', 'Market Top/Transition', 'Bear Market (Early Phase)', 'Bear Market (Mature/Deep Phase)', 'Market Bottom/Recovery', and 'Range-Bound/Consolidation'. Identifying the current regime helps investors understand broader market context and adjust strategies accordingly, as different investment approaches perform better in different regime phases.",
    "relevance": "medium",
    "userLevel": "intermediate"
  },
  {
    "type": "faq",
    "question": "How do I log in and keep my account secure?",
    "answer": "Tap \"Continue with Google\" on the sign-in screen; SMA uses Google OAuth so your credentials never touch our servers. Multi-factor authentication via your Google account is fully supported.",
    "relevance": "medium",
    "userLevel": "all"
  },
  {
    "type": "faq",
    "question": "Where does SMA get its data and how reliable is it?",
    "answer": "Price and volume come from consolidated U.S. exchange feeds; fundamentals from SEC filings; factor data from option-implied volatility and quantitative data vendors. Historical accuracy of the ML models, shown as ta1_accuracy in advanced views, averages 68–75% depending on horizon.",
    "relevance": "medium",
    "userLevel": "all"
  },
  {
    "type": "faq",
    "question": "What happens when Money-Flow, Performance, and Risk disagree?",
    "answer": "If Risk is HIGH the app caps suggested equity exposure even when Money-Flow shows STRONG INFLOW. Explanations appear in each report so you understand any conflicts before acting.",
    "relevance": "medium",
    "userLevel": "intermediate"
  },
  {
    "type": "faq",
    "question": "How are the 'ideal / min / max' allocation bands chosen?",
    "answer": "Allocation bands come from historical Sharpe optimisation under each profile's risk budget and are nudged daily by Money-Flow, Performance, and Risk trends. A bullish, improving mix widens the max; a bearish, deteriorating mix narrows it.",
    "relevance": "medium",
    "userLevel": "intermediate"
  },
  {
    "type": "faq",
    "question": "Why does my profile show 'Risk: HIGH' even when Performance is GAIN?",
    "answer": "Performance tracks price momentum, while Risk flags downside volatility. A rally on thin volume or with large intraday swings can score GAIN but still be HIGH risk—SMA trims recommended exposure until volatility subsides.",
    "relevance": "medium",
    "userLevel": "intermediate"
  },
  {
    "type": "faq",
    "question": "Does the app execute trades for me?",
    "answer": "No—SMA is research and decision-support only. You place trades with your broker. You can, however, export any screener or watch-list to CSV for quick order entry.",
    "relevance": "medium",
    "userLevel": "all"
  },
  {
    "type": "faq",
    "question": "Can I export data from SMA?",
    "answer": "While SMA primarily focuses on providing interactive analysis within the platform, you can save and export certain results and reports. The specific export capabilities depend on your subscription tier, with premium tiers offering more export options. Check the download or export options in each section of the interface for available export formats.",
    "relevance": "low",
    "userLevel": "intermediate"
  },
  {
    "type": "glossary",
    "term": "Rating",
    "definition": "A classification indicating the expected market direction or condition. Ratings vary by context and can include BULLISH (strongly positive outlook), MODERATELY BULLISH (positive outlook with some caution), NEUTRAL (balanced outlook), MODERATELY BEARISH (negative outlook with some optimism), and BEARISH (strongly negative outlook). Other rating scales are used for specific metrics like risk, money flow, and performance.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Market Rating",
    "definition": "One-word summary—BULLISH, MODERATELY BULLISH, MODERATELY BEARISH, or BEARISH—derived from a weighted blend of factor ratings.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Trend",
    "definition": "The direction of change in a particular metric or market condition over time. SMA classifies trends as IMPROVING (getting better), DETERIORATING (getting worse), or STEADY (relatively unchanged). Trend analysis helps investors understand momentum and potential future direction.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Money Flow",
    "definition": "The measure of capital moving into or out of a stock, sector, or market as a whole. Strong positive money flow indicates buying pressure that may drive prices higher, while strong negative money flow suggests selling pressure that may drive prices lower. SMA analyzes money flow patterns to identify where capital is being allocated or withdrawn in the market.",
    "relevance": "medium",
    "userLevel": "intermediate"
  },
  {
    "type": "glossary",
    "term": "Money-Flow Rating",
    "definition": "Qualitative label—STRONG INFLOW / INFLOW / FLAT / OUTFLOW / STRONG OUTFLOW—assigned from the % gap between AD and ADs50 across market, sector, and cap slices.",
    "relevance": "medium",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Performance Rating",
    "definition": "STRONG GAIN / GAIN / FLAT / LOSS / STRONG LOSS derived from recent price changes and expected returns.",
    "relevance": "medium",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Risk Rating",
    "definition": "LOW / MED-LOW / MED-HIGH / HIGH based on downside deviation, Value-at-Risk, and option-implied volatility versus historical percentiles.",
    "relevance": "medium",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Risk Adjusted",
    "definition": "A measure that considers both the potential return of an investment and its risk level. Risk-adjusted metrics allow for fairer comparisons between investments with different volatility profiles. Higher risk-adjusted returns indicate better performance per unit of risk taken.",
    "relevance": "medium",
    "userLevel": "intermediate"
  },
  {
    "type": "glossary",
    "term": "Sharpe Ratio",
    "definition": "A risk-adjusted performance measure calculated by dividing an investment's excess return (return above risk-free rate) by its standard deviation of returns. A higher Sharpe ratio indicates better risk-adjusted performance. SMA uses this metric among others to assess investment quality across different time horizons.",
    "relevance": "medium",
    "userLevel": "advanced"
  },
  {
    "type": "glossary",
    "term": "Sortino Ratio",
    "definition": "A variation of the Sharpe ratio that only considers downside volatility (harmful volatility) rather than total volatility. It measures excess return per unit of downside risk, providing insight into an investment's performance during negative periods. A higher Sortino ratio indicates better risk-adjusted returns regarding downside risk.",
    "relevance": "low",
    "userLevel": "advanced"
  },
  {
    "type": "glossary",
    "term": "Price Target",
    "definition": "The projected future price level of a security based on analysis and forecasting. SMA provides price targets across different time horizons (Short-Term, Mid-Term, Long-Term) along with probability assessments of reaching those targets. Price targets are expressed both as absolute values and as percentage gain potential from current price.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "PriceTarget & Bounds",
    "definition": "Model-derived expected price plus a 90% confidence band (Lower/UpperBound) for the chosen horizon. Probability-of-Hit shows historic hit-rates.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Gain_adjusted",
    "definition": "Expected PriceTarget multiplied by Probability-of-Hit—gives risk-weighted upside used in cross-ticker rankings.",
    "relevance": "medium",
    "userLevel": "intermediate"
  },
  {
    "type": "glossary",
    "term": "Heatmap",
    "definition": "A visual representation of data where values are depicted by colors. In SMA, heatmaps show relative performance, money flow, momentum, or risk across different market segments like sectors or market capitalization groups. Red typically indicates negative values or outperformance, while green indicates positive values or outperformance. Intensity of color reflects magnitude of the measure.",
    "relevance": "medium",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Moving Average",
    "definition": "A calculation used to analyze data points by creating a series of averages of different subsets of the full data set. Common in technical analysis, moving averages help smooth out price action and identify trends. SMA uses various moving averages including SMA (Simple Moving Average) and EMA (Exponential Moving Average) across different time periods (such as 20-day, 50-day, and 200-day).",
    "relevance": "medium",
    "userLevel": "intermediate"
  },
  {
    "type": "glossary",
    "term": "EMA-20 / EMA-50 / EMA-200",
    "definition": "Exponential moving averages over 20, 50, and 200 trading days—classic trend references included in every ticker record.",
    "relevance": "medium",
    "userLevel": "intermediate"
  },
  {
    "type": "glossary",
    "term": "Market Cap Category",
    "definition": "A classification of companies based on their market capitalization (share price multiplied by number of shares outstanding). SMA uses categories like Mega (typically >$200B), Large ($10B-$200B), Mid ($2B-$10B), and Small ($300M-$2B). Market cap categories often exhibit different risk-return characteristics and sector concentrations.",
    "relevance": "medium",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Allocation",
    "definition": "The recommended distribution of investment capital across different asset classes, sectors, or market cap categories. SMA provides allocation recommendations with minimum, maximum, and ideal percentages based on current market conditions and your investment profile. These allocations help guide portfolio construction and risk management.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Screener",
    "definition": "A tool that filters stocks based on specific criteria to identify investment opportunities matching certain parameters. SMA offers two types: Pandas screeners (using quantitative rules and metrics) and AI screeners (using natural language descriptions of desired stock characteristics). Screeners help discover stocks matching your investment criteria without manually reviewing the entire market.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Profile Default Screener",
    "definition": "Non-deletable screener whose underlying rule always mirrors the active profile's horizon, risk appetite, and strategy; updates automatically if you edit the profile.",
    "relevance": "medium",
    "userLevel": "intermediate"
  },
  {
    "type": "glossary",
    "term": "Watchlist",
    "definition": "A personalized list of stocks you want to monitor. Unlike screeners which find stocks based on criteria, watchlists contain specific stocks you manually select. In SMA, watchlists can be organized by investment profile and marked as favorites for quick access. They allow you to track performance, ratings, and changes for your stocks of interest.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Short Term",
    "definition": "In SMA, Short Term refers to an investment time horizon of approximately 1 month. Short term analysis focuses on immediate market movements, technical factors, momentum, and near-term catalysts. This horizon is suitable for active traders and tactical adjustments to positions.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Mid Term",
    "definition": "In SMA, Mid Term refers to an investment time horizon of approximately 4.5 months. Mid term analysis balances technical and fundamental factors, with increased weight on sector trends, earnings expectations, and intermediate economic indicators. This horizon is suitable for swing traders and medium-term position adjustments.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Long Term",
    "definition": "In SMA, Long Term refers to an investment time horizon of approximately 1 year. Long term analysis emphasizes fundamental factors, valuation, company positioning, growth prospects, and broader economic trends. This horizon is suitable for position traders, investors focused on capital appreciation, and those with lower trading frequency.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "ShortTerm / MidTerm / LongTerm",
    "definition": "Three horizons SMA uses consistently: ≈1 month, 4.5 months, and 1 year. Each horizon has its own Rating, Ranking, Price-Target, Probability-of-Hit, Gain_adjusted, Sharpe, and Sortino values.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Relative Strength",
    "definition": "A measure of how a security's price performance compares to a benchmark (like a sector or the broader market such as SPY). In SMA, relative strength metrics show if a stock is outperforming or underperforming its peers or the market as a whole. Positive relative strength indicates outperformance, which is often a favorable characteristic for investment candidates.",
    "relevance": "medium",
    "userLevel": "intermediate"
  },
  {
    "type": "glossary",
    "term": "Relative Strength to Sector / SPY",
    "definition": "Ratio of a stock's price change to its sector ETF or to SPY over several look-backs; values above 1 denote out-performance.",
    "relevance": "medium",
    "userLevel": "intermediate"
  },
  {
    "type": "glossary",
    "term": "Technical Analysis",
    "definition": "A methodology that evaluates investments by analyzing statistical trends gathered from trading activity, such as price movement and volume. SMA incorporates various technical analysis metrics including moving averages, momentum indicators, and price patterns to generate predictions and identify potential trading opportunities across different time horizons.",
    "relevance": "medium",
    "userLevel": "intermediate"
  },
  {
    "type": "glossary",
    "term": "Headwinds",
    "definition": "Factors or conditions that may impede a stock's performance or price appreciation. In SMA reports, headwinds are explicitly identified for individual stocks, sectors, and the overall market to highlight potential risks or challenges that could limit upside potential or contribute to downside risk.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Tailwinds",
    "definition": "Factors or conditions that may support a stock's performance or price appreciation. In SMA reports, tailwinds are explicitly identified for individual stocks, sectors, and the overall market to highlight potential catalysts or favorable conditions that could drive upside potential or limit downside risk.",
    "relevance": "high",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Model Aligned",
    "definition": "Indicates that a stock's various signals and indicators are in harmony across different analytical models. When a stock is 'model aligned' in SMA, it means its traditional rating/ranking and AI-powered predictive models are showing consensus, suggesting higher confidence in the projected direction. Non-aligned stocks show discrepancy between different analytical approaches.",
    "relevance": "medium",
    "userLevel": "intermediate"
  },
  {
    "type": "glossary",
    "term": "Market Regime",
    "definition": "Cycle label used by SMA such as Bull Market (Early), Market Top/Transition, or Bear Market. Helps set realistic return expectations.",
    "relevance": "medium",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "ProjectedTrend (Short/Mid/Long)",
    "definition": "Model forecast of trend direction—UP / STEADY / DOWN—shown for each horizon in Market and Sector reports.",
    "relevance": "medium",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Tickers_for_banner",
    "definition": "Curated list of headline tickers & ETFs with notable daily moves that appear at the top of the dashboard.",
    "relevance": "low",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Accumulation-Distribution (AD)",
    "definition": "A volume-weighted price-flow gauge that reveals whether shares are being accumulated (bought) or distributed (sold). SMA compares today's AD to its 50-day average (ADs50) to drive Money-Flow ratings.",
    "relevance": "medium",
    "userLevel": "intermediate"
  },
  {
    "type": "glossary",
    "term": "ADs50",
    "definition": "The 50-day simple moving average of the AD line. When current AD sits well above ADs50 the Money-Flow report labels the market or sector as INFLOW or STRONG INFLOW depending on the gap.",
    "relevance": "medium",
    "userLevel": "intermediate"
  },
  {
    "type": "glossary",
    "term": "ADs50_gap",
    "definition": "Percentage distance between today's AD and its 50-day average—key trigger for STRONG INFLOW or STRONG OUTFLOW labels.",
    "relevance": "low",
    "userLevel": "intermediate"
  },
  {
    "type": "glossary",
    "term": "Rebalance Frequency",
    "definition": "Suggested cadence for refreshing allocations and screen criteria: Daily (Ultra-Aggressive), Bi-Weekly → Monthly (Aggressive Growth), or Quarterly (Balanced, Income, Value).",
    "relevance": "medium",
    "userLevel": "all"
  },
  {
    "type": "glossary",
    "term": "Institutional Holding",
    "definition": "Percentage of outstanding shares owned by institutional 13-F filers; rising values may confirm smart-money accumulation.",
    "relevance": "medium",
    "userLevel": "intermediate"
  },
  {
    "type": "glossary",
    "term": "ta1_accuracy",
    "definition": "Rolling hit-rate of SMA's technical-analysis model, displayed in advanced ticker views and used to weight Gain_adjusted.",
    "relevance": "low",
    "userLevel": "advanced"
  },
  {
    "type": "glossary",
    "term": "SMA_20_price_volume_product",
    "definition": "20-day simple moving average of price × volume—helps screen for sustained accumulation.",
    "relevance": "low",
    "userLevel": "advanced"
  }
]

// Type assertion to ensure the imported data matches our expected type
export const faqAndGlossaryItems: FAQOrGlossaryItem[] = faqAndGlossaryData as FAQOrGlossaryItem[];

// Helper functions to filter the data
export const getFAQItems = (): FAQItem[] => {
  return faqAndGlossaryItems.filter(item => item.type === 'faq') as FAQItem[];
};

export const getGlossaryItems = (): GlossaryItem[] => {
  return faqAndGlossaryItems.filter(item => item.type === 'glossary') as GlossaryItem[];
};

// Get items by relevance
export const getItemsByRelevance = (items: FAQOrGlossaryItem[], relevance: Relevance): FAQOrGlossaryItem[] => {
  return items.filter(item => item.relevance === relevance);
};

// Get items by user level
export const getItemsByUserLevel = (items: FAQOrGlossaryItem[], userLevel: UserLevel): FAQOrGlossaryItem[] => {
  // 'all' items should be included for any user level
  return items.filter(item => item.userLevel === userLevel || item.userLevel === 'all');
};

// Search in FAQ and glossary
export const searchItems = (items: FAQOrGlossaryItem[], query: string): FAQOrGlossaryItem[] => {
  const lowercaseQuery = query.toLowerCase();
  return items.filter(item => {
    if (item.type === 'faq') {
      return (
        item.question.toLowerCase().includes(lowercaseQuery) ||
        item.answer.toLowerCase().includes(lowercaseQuery)
      );
    } else {
      return (
        item.term.toLowerCase().includes(lowercaseQuery) ||
        item.definition.toLowerCase().includes(lowercaseQuery)
      );
    }
  });
};
